import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Switch,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  Chip,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Breadcrumbs,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Close as CloseIcon,
  DragIndicator as GripIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { format as formatDate } from 'date-fns';
import { EasyCheckButton } from '../../common/Controls';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { useSnackbar } from '../../../hooks/useSnackbar';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';
import { companyPreferencesService } from '../../../services/settings/companyPreferencesService';
import { voucherService, formatVoucherNumber } from '../../../services/cheques/voucherService';

// Full-screen Payment Voucher designer (/preferences/voucher-design).
// Frontend only: reuses GET/PUT api/vouchers/settings and the ['voucherSettings'] query key.
// Layout: sticky top bar · section nav · settings pane · sticky true-A4 preview.

// ---------- layout ----------
const Page = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: theme.palette.grey[50],
}));

const TopBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  padding: theme.spacing(1.25, 3),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  flex: 'none',
}));

const Workspace = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(3, 3.5),
  display: 'grid',
  gridTemplateColumns: '220px minmax(460px, 640px) minmax(420px, 1fr)',
  gap: theme.spacing(3),
  alignItems: 'start',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '180px 1fr',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    padding: theme.spacing(2),
  },
}));

const SectionNav = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  [theme.breakpoints.down('sm')]: {
    position: 'static',
    flexDirection: 'row',
    overflowX: 'auto',
  },
}));

const NavItem = styled('button', { shouldForwardProp: (p) => p !== 'active' })(({ theme, active }) => ({
  display: 'grid',
  gridTemplateColumns: '22px 1fr',
  gap: theme.spacing(1.25),
  alignItems: 'start',
  textAlign: 'left',
  padding: theme.spacing(1.1, 1.25),
  borderRadius: 8,
  border: 0,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 13,
  color: active ? theme.palette.primary.dark : theme.palette.text.secondary,
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, active ? 0.12 : 0.05) },
  '& .dot': {
    width: 22,
    height: 22,
    borderRadius: 6,
    display: 'grid',
    placeItems: 'center',
    fontSize: 11,
    fontWeight: 700,
    color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
    backgroundColor: active ? theme.palette.primary.main : theme.palette.background.paper,
    border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  },
  '& small': { display: 'block', fontSize: 11.5, opacity: 0.8, marginTop: 1 },
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'auto', whiteSpace: 'nowrap', '& small': { display: 'none' } },
}));

const FormCol = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minWidth: 0,
}));

const Section = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5, 2.75),
  scrollMarginTop: theme.spacing(1),
}));

const SubHead = styled(Typography)(({ theme }) => ({
  fontSize: 11.5,
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  margin: theme.spacing(2.25, 0, 1.25),
}));

const Row = styled(Box, { shouldForwardProp: (p) => p !== 'cols' })(({ theme, cols = 1 }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const Readout = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: theme.spacing(0.5, 1.75),
  alignItems: 'center',
  backgroundColor: theme.palette.grey[50],
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: 10,
  padding: theme.spacing(1.5, 1.75),
  marginTop: theme.spacing(1.75),
}));

const LogoZone = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '120px 1fr',
  gap: theme.spacing(1.75),
  alignItems: 'center',
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: 10,
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const SwitchRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.75, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

// ---------- preview (true A4; every size scales with sheet width via container units) ----------
const PreviewCol = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  minWidth: 0,
  [theme.breakpoints.down('lg')]: { gridColumn: '1 / -1', position: 'static' },
}));

const Canvas = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  borderRadius: 12,
  padding: theme.spacing(2.75),
  display: 'grid',
  justifyItems: 'center',
  overflow: 'auto',
  maxHeight: 'calc(100vh - 64px - 150px)',
}));

const Sheet = styled(Box)(({ theme }) => ({
  aspectRatio: '210 / 297',
  width: '100%',
  backgroundColor: '#fff',
  color: '#111',
  boxShadow: theme.shadows[8],
  containerType: 'inline-size',
  overflow: 'hidden',
  fontFamily: 'Helvetica, Arial, sans-serif',
  '& .page': { padding: '6.7cqw', display: 'flex', flexDirection: 'column', height: '100%' },
  '& .hdr': { display: 'flex', gap: '3cqw', alignItems: 'flex-start' },
  '& .logo': { width: '24cqw', height: '9cqw', flex: 'none', display: 'grid', placeItems: 'center', border: '1px dashed #c8c8c8', color: '#999', fontSize: '1.9cqw' },
  '& .logo img': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  '& .lines': { flex: 1 },
  '& .co': { fontWeight: 700, fontSize: '2.9cqw', marginBottom: '0.6cqw' },
  '& .ln': { fontSize: '1.85cqw', lineHeight: 1.45, letterSpacing: '0.02em', minHeight: '1.85cqw' },
  '& .meta': { display: 'grid', gridTemplateColumns: '30fr 40fr 30fr', alignItems: 'end', marginTop: '3.2cqw', gap: '2cqw' },
  '& .kv': { fontSize: '1.9cqw', lineHeight: 1.7 },
  '& .ttl': { textAlign: 'center', fontWeight: 700, fontSize: '2.7cqw', alignSelf: 'center', letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  '& .amt': { justifySelf: 'end', width: '80%' },
  '& .cur': { fontSize: '1.8cqw', marginBottom: '0.4cqw' },
  '& .box': { border: '0.3cqw solid #111', padding: '0.9cqw 1.2cqw', textAlign: 'right', fontWeight: 700, fontSize: '2.3cqw' },
  '& .rules': { marginTop: '2.5cqw' },
  '& .rule': { borderBottom: '0.22cqw dotted #555', padding: '2.2cqw 0 0.8cqw', fontSize: '1.95cqw', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  '& .rule.wrap': { whiteSpace: 'normal' },
  '& .chq': { display: 'grid', gridTemplateColumns: '30fr 42fr 28fr', gap: '3cqw' },
  '& .sigs': { display: 'grid', gap: '4cqw', marginTop: 'auto', paddingTop: '14cqw' },
  '& .sigs div': { borderTop: '0.25cqw solid #111', margin: '0 2cqw', paddingTop: '1cqw', textAlign: 'center', fontSize: '1.8cqw' },
  '& .foot': { textAlign: 'center', color: '#777', fontSize: '1.6cqw', marginTop: '4cqw' },
  '& b': { fontWeight: 700 },
}));

// ---------- constants / helpers ----------
const SECTIONS = [
  { id: 'numbering', label: 'Numbering' },
  { id: 'header', label: 'Header & logo' },
  { id: 'labels', label: 'Title & labels' },
  { id: 'fields', label: 'Fields & format' },
  { id: 'signatures', label: 'Signatures & footer' },
];

const DATE_FORMATS = [
  { value: 'dd-MMM-yyyy', label: '24-Sep-2026' },
  { value: 'dd/MM/yyyy', label: '24/09/2026' },
  { value: 'dd-MM-yyyy', label: '24-09-2026' },
  { value: 'yyyy-MM-dd', label: '2026-09-24' },
  { value: 'MMMM d, yyyy', label: 'September 24, 2026' },
];

const MAX_LOGO_BYTES = 200 * 1024;
const LOGO_MAX_W = 600;
const LOGO_MAX_H = 200;
const MAX_HEADER_LINES = 6;

const prepareLogo = (file) =>
  new Promise((resolve, reject) => {
    if (!/^image\/(png|jpeg)$/i.test(file.type)) {
      reject(new Error('Logo must be a PNG or JPEG image'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the selected file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not decode the selected image'));
      img.onload = () => {
        const scale = Math.min(1, LOGO_MAX_W / img.width, LOGO_MAX_H / img.height);
        const width = Math.max(1, Math.round(img.width * scale));
        const height = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (file.type === 'image/jpeg') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(img, 0, 0, width, height);
        const contentType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
        const dataUrl = contentType === 'image/jpeg' ? canvas.toDataURL('image/jpeg', 0.85) : canvas.toDataURL('image/png');
        const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
        if (Math.floor((base64.length * 3) / 4) > MAX_LOGO_BYTES) {
          reject(new Error('Logo is too large even after resizing (max 200 KB). Use a simpler image.'));
          return;
        }
        resolve({ base64, contentType });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const toForm = (settings) => ({
  prefix: settings.prefix,
  numberPadding: settings.numberPadding,
  startingNumber: settings.nextNumber,
  design: { ...settings.design, headerLines: [...(settings.design.headerLines || [''])], signatures: [...(settings.design.signatures || [])] },
  logoBase64: settings.logoBase64,
  logoContentType: settings.logoContentType,
  logoChanged: false,
  removeLogo: false,
});

const segValue = (setter) => (event, value) => {
  if (value !== null && value !== undefined) setter(value);
};

// ---------- page ----------
const VoucherDesignPage = () => {
  const queryClient = useQueryClient();
  const { user } = useEasyCheck();
  const isAdmin = user?.role === 'Admin';
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const workspaceRef = useRef(null);
  const sectionRefs = useRef({});

  const { data: preferences } = useQuery({
    queryKey: ['companyPreferences'],
    queryFn: async () => (await companyPreferencesService.getPreferences()).data,
  });
  const moduleOn = Boolean(preferences?.enableVoucherPrinting);

  const { data: settings, isLoading, isError } = useQuery({
    queryKey: ['voucherSettings'],
    queryFn: () => voucherService.getSettings(),
  });

  const [form, setForm] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [activeSection, setActiveSection] = useState('numbering');
  const [zoom, setZoom] = useState('100');

  useEffect(() => {
    if (settings) setForm(toForm(settings));
  }, [settings]);

  // scroll-spy inside the workspace scroll region
  useEffect(() => {
    const root = workspaceRef.current;
    if (!root || !form) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { root, rootMargin: '-20% 0px -65% 0px' }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [form]);

  const saveMutation = useMutation({
    mutationFn: (payload) => voucherService.saveSettings(payload),
    onSuccess: (saved) => {
      queryClient.setQueryData(['voucherSettings'], saved);
      queryClient.invalidateQueries({ queryKey: ['voucherSettings'] });
      showSuccess('Voucher design saved');
    },
    onError: (error) => showError(error?.message || 'Failed to save voucher design'),
  });

  const hasChanges = useMemo(() => {
    if (!form || !settings) return false;
    const strip = (f) => ({ ...f, logoBase64: undefined });
    return JSON.stringify(strip(form)) !== JSON.stringify(strip(toForm(settings))) || form.logoChanged || form.removeLogo;
  }, [form, settings]);

  const canEdit = isAdmin && moduleOn;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setDesign = (key, value) => setForm((prev) => ({ ...prev, design: { ...prev.design, [key]: value } }));
  const setHeaderLine = (index, value) =>
    setForm((prev) => {
      const lines = [...prev.design.headerLines];
      lines[index] = value.slice(0, 120);
      return { ...prev, design: { ...prev.design, headerLines: lines } };
    });
  const removeHeaderLine = (index) =>
    setForm((prev) => {
      const lines = prev.design.headerLines.filter((_, i) => i !== index);
      return { ...prev, design: { ...prev.design, headerLines: lines.length ? lines : [''] } };
    });
  const addHeaderLine = () =>
    setForm((prev) =>
      prev.design.headerLines.length >= MAX_HEADER_LINES
        ? prev
        : { ...prev, design: { ...prev.design, headerLines: [...prev.design.headerLines, ''] } }
    );
  const setSignature = (index, value) =>
    setForm((prev) => {
      const sigs = [0, 1, 2].map((i) => prev.design.signatures[i] || '');
      sigs[index] = value.slice(0, 60);
      return { ...prev, design: { ...prev.design, signatures: sigs } };
    });

  const handleLogoFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setLogoBusy(true);
    try {
      const { base64, contentType } = await prepareLogo(file);
      setForm((prev) => ({ ...prev, logoBase64: base64, logoContentType: contentType, logoChanged: true, removeLogo: false }));
    } catch (error) {
      showError(error.message);
    } finally {
      setLogoBusy(false);
    }
  };

  const handleRemoveLogo = () =>
    setForm((prev) => ({ ...prev, logoBase64: null, logoContentType: null, logoChanged: false, removeLogo: true }));

  const handleResetDesign = () => {
    setForm((prev) => {
      const defaults = voucherService.getDefaultDesign();
      defaults.headerLines = [prev.design.headerLines[0] || ''];
      return { ...prev, design: defaults };
    });
  };

  const handleSave = () => {
    if (!form) return;
    const startingNumber = Number(form.startingNumber);
    if (!Number.isInteger(startingNumber) || startingNumber < 1) {
      showError('Next number must be a whole number of 1 or more');
      return;
    }
    const headerLines = form.design.headerLines.map((l) => (l || '').trim()).slice(0, MAX_HEADER_LINES);
    const signatures = (form.design.signatures || []).map((s) => (s || '').trim()).filter(Boolean).slice(0, 3);
    const payload = {
      prefix: (form.prefix || '').trim(),
      numberPadding: Number(form.numberPadding) || 0,
      startingNumber,
      design: { ...form.design, headerLines, signatures },
      removeLogo: Boolean(form.removeLogo),
    };
    if (form.logoChanged && form.logoBase64) {
      payload.logoBase64 = form.logoBase64;
      payload.logoContentType = form.logoContentType;
    }
    saveMutation.mutate(payload);
  };

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  };

  // ---- derived preview values ----
  const nextNumberText = form ? formatVoucherNumber(form.prefix, form.startingNumber, Number(form.numberPadding) || 0) : '';
  const previewDate = useMemo(() => {
    if (!form) return '';
    try {
      return formatDate(new Date(), form.design.dateFormat || 'dd-MMM-yyyy');
    } catch (e) {
      return formatDate(new Date(), 'dd-MMM-yyyy');
    }
  }, [form]);
  const previewWords = form?.design?.amountWordsStyle === 'title' ? 'One Thousand Dirhams Only' : 'ONE THOUSAND DIRHAMS ONLY';
  const sheetMaxWidth = { fit: 440, 100: 560, 150: 760 }[zoom] || 560;

  if (isLoading || !form) {
    return (
      <Page>
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      </Page>
    );
  }

  const d = form.design;
  const sigs = [0, 1, 2].map((i) => d.signatures[i] || '');
  const visibleSigs = sigs.filter(Boolean);
  const navSummary = {
    numbering: `${nextNumberText} next`,
    header: `${d.headerLines.filter(Boolean).length} line${d.headerLines.filter(Boolean).length === 1 ? '' : 's'} · ${form.logoBase64 ? 'logo' : 'no logo'}`,
    labels: d.title || 'Untitled',
    fields: `${[d.showAmountBox, d.showChequeNo, d.showBank, d.showDated, d.showBeing].filter(Boolean).length} of 5 shown`,
    signatures: `${visibleSigs.length} caption${visibleSigs.length === 1 ? '' : 's'}`,
  };
  const logoSrc = form.logoBase64 ? `data:${form.logoContentType || 'image/png'};base64,${form.logoBase64}` : null;

  const field = (props) => <TextField size="small" fullWidth disabled={!canEdit} {...props} />;

  return (
    <Page>
      {/* ---------- top bar ---------- */}
      <TopBar>
        <Breadcrumbs sx={{ fontSize: 13 }}>
          <Link component={RouterLink} to="/preferences" underline="hover" color="text.secondary">Preferences</Link>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }} color="text.primary">Voucher Design</Typography>
        </Breadcrumbs>
        <Chip
          size="small"
          label={moduleOn ? 'Payment Voucher Printing is on' : 'Payment Voucher Printing is off'}
          color={moduleOn ? 'success' : 'default'}
          variant={moduleOn ? 'filled' : 'outlined'}
          sx={{ fontWeight: 600 }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          Next voucher <Box component="strong" sx={{ color: 'text.primary', fontSize: 15 }}>{nextNumberText}</Box>
          {settings?.hasIssuedVouchers && <> · issued up to {settings.lastIssuedNumber}</>}
        </Typography>
        {isAdmin && (
          <>
            <EasyCheckButton buttonType="action" onClick={handleResetDesign} disabled={!canEdit}>
              Reset to defaults
            </EasyCheckButton>
            <EasyCheckButton buttonType="form" onClick={handleSave} loading={saveMutation.isPending} disabled={!canEdit || !hasChanges}>
              Save design
            </EasyCheckButton>
          </>
        )}
      </TopBar>

      <Workspace ref={workspaceRef}>
        {/* ---------- section nav ---------- */}
        <SectionNav>
          {SECTIONS.map((s, i) => (
            <NavItem key={s.id} type="button" active={activeSection === s.id} onClick={() => scrollTo(s.id)}>
              <span className="dot">{i + 1}</span>
              <span>
                {s.label}
                <small>{navSummary[s.id]}</small>
              </span>
            </NavItem>
          ))}
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider', fontSize: 12, color: 'text.secondary', lineHeight: 1.5, display: { xs: 'none', sm: 'block' } }}>
            <Box component="b" sx={{ color: 'text.primary' }}>How numbering works</Box>
            <br />
            A number is assigned the first time a voucher is printed and reused on every reprint. You can only move the start forward once vouchers exist.
          </Box>
        </SectionNav>

        {/* ---------- settings ---------- */}
        <FormCol>
          {!moduleOn && (
            <Alert severity="info">
              Payment Voucher Printing is switched off. Turn it on under{' '}
              <Link component={RouterLink} to="/preferences">Preferences → Feature Modules</Link> and save before editing the design.
            </Alert>
          )}
          {isError && <Alert severity="warning">Could not load saved voucher settings; showing defaults.</Alert>}
          {!isAdmin && <Alert severity="info">Only administrators can change the voucher design.</Alert>}

          <Section id="numbering" ref={(el) => (sectionRefs.current.numbering = el)}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Numbering</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Every cheque gets one voucher number, assigned on first print.</Typography>
            <Row cols={3}>
              {field({ id: 'voucher-prefix', label: 'Prefix', value: form.prefix, onChange: (e) => setField('prefix', e.target.value.slice(0, 20)), inputProps: { maxLength: 20 } })}
              {field({
                id: 'voucher-next-number',
                label: 'Next number',
                type: 'number',
                value: form.startingNumber,
                onChange: (e) => setField('startingNumber', e.target.value),
                inputProps: { min: 1, step: 1 },
                helperText: settings?.hasIssuedVouchers ? `Minimum ${settings.nextNumber} (${settings.lastIssuedNumber} already issued)` : 'No vouchers issued yet',
              })}
              {field({
                id: 'voucher-digits',
                label: 'Digits',
                select: true,
                value: form.numberPadding,
                onChange: (e) => setField('numberPadding', Number(e.target.value)),
                children: [0, 3, 4, 5, 6].map((n) => <MenuItem key={n} value={n}>{n === 0 ? 'None' : n}</MenuItem>),
              })}
            </Row>
            <Readout>
              <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '0.02em', gridRow: 'span 2', fontVariantNumeric: 'tabular-nums' }}>{nextNumberText}</Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Next voucher</Typography>
              <Typography variant="caption" color="text.primary">
                Then {formatVoucherNumber(form.prefix, Number(form.startingNumber) + 1, Number(form.numberPadding) || 0)}, {formatVoucherNumber(form.prefix, Number(form.startingNumber) + 2, Number(form.numberPadding) || 0)} … Prefix and digits can change at any time; issued numbers keep their original format.
              </Typography>
            </Readout>
          </Section>

          <Section id="header" ref={(el) => (sectionRefs.current.header = el)}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Header &amp; logo</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Printed at the top of the voucher. The first line is your company name.</Typography>
            <SubHead>Logo</SubHead>
            <LogoZone>
              <Box sx={{ height: 56, borderRadius: 1.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider', display: 'grid', placeItems: 'center', color: 'text.secondary', fontSize: 11, overflow: 'hidden', p: 0.5 }}>
                {logoSrc ? <Box component="img" alt="Voucher logo" src={logoSrc} sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} /> : 'No logo'}
              </Box>
              <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>PNG or JPEG</Typography>
                Resized to 600 × 200 px, max 200 KB. Shown at the top of the voucher.
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input ref={fileInputRef} type="file" accept="image/png,image/jpeg" hidden onChange={handleLogoFile} />
                  <EasyCheckButton buttonType="action" onClick={() => fileInputRef.current?.click()} disabled={!canEdit || logoBusy}>
                    <UploadIcon fontSize="small" sx={{ mr: 0.5 }} /> {logoBusy ? 'Processing…' : logoSrc ? 'Replace logo' : 'Upload logo'}
                  </EasyCheckButton>
                  {logoSrc && (
                    <EasyCheckButton buttonType="action" color="danger" onClick={handleRemoveLogo} disabled={!canEdit}>
                      Remove
                    </EasyCheckButton>
                  )}
                  <ToggleButtonGroup size="small" exclusive value={d.logoPosition} onChange={segValue((v) => setDesign('logoPosition', v))} disabled={!canEdit || !d.showLogo}>
                    <ToggleButton value="left">Left</ToggleButton>
                    <ToggleButton value="right">Right</ToggleButton>
                  </ToggleButtonGroup>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Switch size="small" checked={Boolean(d.showLogo)} onChange={(e) => setDesign('showLogo', e.target.checked)} disabled={!canEdit} />
                    <Typography variant="body2">Show logo</Typography>
                  </Box>
                </Box>
              </Box>
            </LogoZone>

            <SubHead>Header lines</SubHead>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {d.headerLines.map((line, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '20px 1fr auto', gap: 1, alignItems: 'center' }}>
                  <GripIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  <TextField
                    id={`voucher-header-line-${i}`}
                    size="small"
                    fullWidth
                    value={line}
                    placeholder={i === 0 ? 'Company name' : 'P O Box, city, phone, fax…'}
                    onChange={(e) => setHeaderLine(i, e.target.value)}
                    disabled={!canEdit}
                    inputProps={{ maxLength: 120, style: i === 0 ? { fontWeight: 600 } : undefined }}
                  />
                  <Tooltip title="Remove line">
                    <span>
                      <IconButton size="small" onClick={() => removeHeaderLine(i)} disabled={!canEdit || d.headerLines.length === 1} aria-label="Remove line">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
              <Box>
                <EasyCheckButton buttonType="action" onClick={addHeaderLine} disabled={!canEdit || d.headerLines.length >= MAX_HEADER_LINES}>
                  <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Add line
                </EasyCheckButton>
              </Box>
            </Box>
            <SubHead>Header alignment</SubHead>
            <ToggleButtonGroup size="small" exclusive value={d.headerAlign} onChange={segValue((v) => setDesign('headerAlign', v))} disabled={!canEdit}>
              <ToggleButton value="left">Left</ToggleButton>
              <ToggleButton value="center">Center</ToggleButton>
              <ToggleButton value="right">Right</ToggleButton>
            </ToggleButtonGroup>
          </Section>

          <Section id="labels" ref={(el) => (sectionRefs.current.labels = el)}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Title &amp; labels</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Rename any printed label to match your stationery. Leave a label blank to print the value alone.</Typography>
            {field({ id: 'voucher-title', label: 'Voucher title', value: d.title, onChange: (e) => setDesign('title', e.target.value.slice(0, 60)) })}
            <SubHead>Voucher</SubHead>
            <Row cols={2}>
              {field({ id: 'voucher-label-no', label: 'Voucher no.', value: d.labelVoucherNo, onChange: (e) => setDesign('labelVoucherNo', e.target.value.slice(0, 40)) })}
              {field({ id: 'voucher-label-date', label: 'Voucher date', value: d.labelDate, onChange: (e) => setDesign('labelDate', e.target.value.slice(0, 40)) })}
            </Row>
            <SubHead>Payee</SubHead>
            <Row cols={2}>
              {field({ id: 'voucher-label-paidto', label: 'Paid to', value: d.labelPaidTo, onChange: (e) => setDesign('labelPaidTo', e.target.value.slice(0, 40)) })}
              {field({ id: 'voucher-label-sumof', label: 'Amount in words', value: d.labelSumOf, onChange: (e) => setDesign('labelSumOf', e.target.value.slice(0, 40)) })}
            </Row>
            <SubHead>Cheque line</SubHead>
            <Row cols={3}>
              {field({ id: 'voucher-label-chequeno', label: 'Cheque no.', value: d.labelChequeNo, onChange: (e) => setDesign('labelChequeNo', e.target.value.slice(0, 40)) })}
              {field({ id: 'voucher-label-bank', label: 'Bank', value: d.labelBank, onChange: (e) => setDesign('labelBank', e.target.value.slice(0, 40)) })}
              {field({ id: 'voucher-label-dated', label: 'Cheque date', value: d.labelDated, onChange: (e) => setDesign('labelDated', e.target.value.slice(0, 40)) })}
            </Row>
            <SubHead>Narration</SubHead>
            <Row cols={2}>
              {field({ id: 'voucher-label-being', label: 'Narration', value: d.labelBeing, onChange: (e) => setDesign('labelBeing', e.target.value.slice(0, 40)) })}
            </Row>
          </Section>

          <Section id="fields" ref={(el) => (sectionRefs.current.fields = el)}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Fields &amp; format</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Choose what prints and how amounts and dates are written.</Typography>
            <SubHead>Fields shown</SubHead>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3 }}>
              {[
                ['showAmountBox', 'Amount box', 'Numeric amount with currency'],
                ['showChequeNo', 'Cheque number', 'From the cheque record'],
                ['showBank', 'Bank name', 'Account name of the bank'],
                ['showDated', 'Cheque date', 'Dated line'],
                ['showBeing', 'Narration', 'Cheque remarks'],
              ].map(([key, title, desc]) => (
                <SwitchRow key={key}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{title}</Typography>
                    <Typography variant="caption" color="text.secondary">{desc}</Typography>
                  </Box>
                  <Switch size="small" checked={Boolean(d[key])} onChange={(e) => setDesign(key, e.target.checked)} disabled={!canEdit} inputProps={{ 'aria-label': title }} />
                </SwitchRow>
              ))}
            </Box>
            <SubHead>Amount</SubHead>
            <Row cols={2}>
              {field({ id: 'voucher-amount-label', label: 'Amount box label', value: d.amountBoxLabel, placeholder: 'Bank currency code', onChange: (e) => setDesign('amountBoxLabel', e.target.value.slice(0, 20)), helperText: 'Blank = bank currency code (e.g. AED). Example: Dhs.' })}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Amount in words</Typography>
                <ToggleButtonGroup size="small" exclusive value={d.amountWordsStyle} onChange={segValue((v) => setDesign('amountWordsStyle', v))} disabled={!canEdit}>
                  <ToggleButton value="upper">UPPER CASE</ToggleButton>
                  <ToggleButton value="title">Title Case</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Row>
            <SubHead>Dates</SubHead>
            <Row cols={2}>
              {field({
                id: 'voucher-date-format',
                label: 'Date format',
                select: true,
                value: d.dateFormat,
                onChange: (e) => setDesign('dateFormat', e.target.value),
                children: DATE_FORMATS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>),
              })}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Voucher date</Typography>
                <ToggleButtonGroup size="small" exclusive value={d.voucherDateSource} onChange={segValue((v) => setDesign('voucherDateSource', v))} disabled={!canEdit}>
                  <ToggleButton value="assigned">Date of first print</ToggleButton>
                  <ToggleButton value="chequeDate">Cheque date</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Row>
          </Section>

          <Section id="signatures" ref={(el) => (sectionRefs.current.signatures = el)}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Signatures &amp; footer</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Up to three signature lines. Leave one blank to hide it.</Typography>
            <Row cols={3}>
              {sigs.map((value, i) => (
                <TextField key={i} id={`voucher-signature-${i}`} size="small" fullWidth disabled={!canEdit} label={`Signature ${i + 1}`} value={value} onChange={(e) => setSignature(i, e.target.value)} />
              ))}
            </Row>
            <Box sx={{ mt: 1.5 }}>
              {field({ id: 'voucher-footer', label: 'Footer note (optional)', value: d.footerNote, placeholder: 'e.g. This voucher is valid only with an authorised signature', onChange: (e) => setDesign('footerNote', e.target.value.slice(0, 200)) })}
            </Box>
          </Section>
        </FormCol>

        {/* ---------- preview ---------- */}
        <PreviewCol>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Preview</Typography>
            <Typography variant="caption" color="text.secondary">A4 · sample cheque data · updates as you type</Typography>
            <ToggleButtonGroup size="small" exclusive value={zoom} onChange={segValue(setZoom)} sx={{ ml: 'auto' }}>
              <ToggleButton value="fit">Fit</ToggleButton>
              <ToggleButton value="100">100%</ToggleButton>
              <ToggleButton value="150">150%</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Canvas>
            <Box sx={{ width: '100%', maxWidth: sheetMaxWidth }}>
              <Sheet aria-label="Voucher preview">
                <div className="page">
                  <div className="hdr" style={{ flexDirection: d.logoPosition === 'right' ? 'row-reverse' : 'row' }}>
                    {d.showLogo && (
                      <div className="logo">{logoSrc ? <img alt="" src={logoSrc} /> : 'Logo'}</div>
                    )}
                    <div className="lines" style={{ textAlign: d.headerAlign }}>
                      {d.headerLines.map((line, i) => (
                        <div key={i} className={i === 0 ? 'co' : 'ln'}>{line || (i === 0 ? 'Company name' : '')}</div>
                      ))}
                    </div>
                  </div>
                  <div className="meta">
                    <div className="kv">
                      {d.labelVoucherNo ? `${d.labelVoucherNo} : ` : ''}<b>{nextNumberText}</b>
                      <br />
                      {d.labelDate ? `${d.labelDate} : ` : ''}<b>{previewDate}</b>
                    </div>
                    <div className="ttl">{d.title}</div>
                    <div className="amt">
                      {d.showAmountBox && (
                        <>
                          <div className="cur">{d.amountBoxLabel || 'AED'}</div>
                          <div className="box">1,000.00</div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="rules">
                    <div className="rule">{d.labelPaidTo ? `${d.labelPaidTo} : ` : ''}<b>Sample Supplier LLC</b></div>
                    <div className="rule wrap">{d.labelSumOf ? `${d.labelSumOf} : ` : ''}<b>{previewWords}</b></div>
                    {(d.showChequeNo || d.showBank || d.showDated) && (
                      <div className="chq" style={{ gridTemplateColumns: [d.showChequeNo && '30fr', d.showBank && '42fr', d.showDated && '28fr'].filter(Boolean).join(' ') }}>
                        {d.showChequeNo && <div className="rule">{d.labelChequeNo ? `${d.labelChequeNo} : ` : ''}<b>123456</b></div>}
                        {d.showBank && <div className="rule">{d.labelBank ? `${d.labelBank} : ` : ''}<b>Sample Bank</b></div>}
                        {d.showDated && <div className="rule">{d.labelDated ? `${d.labelDated} : ` : ''}<b>{previewDate}</b></div>}
                      </div>
                    )}
                    {d.showBeing && <div className="rule">{d.labelBeing ? `${d.labelBeing} : ` : ''}<b>Payment against invoice 1001</b></div>}
                    <div className="rule">&nbsp;</div>
                  </div>
                  {visibleSigs.length > 0 && (
                    <div className="sigs" style={{ gridTemplateColumns: `repeat(${visibleSigs.length}, 1fr)` }}>
                      {visibleSigs.map((s, i) => <div key={i}>{s}</div>)}
                    </div>
                  )}
                  {d.footerNote && <div className="foot">{d.footerNote}</div>}
                </div>
              </Sheet>
            </Box>
          </Canvas>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Approximate preview with sample data — the PDF is rendered server-side.
          </Typography>
        </PreviewCol>
      </Workspace>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Page>
  );
};

export default VoucherDesignPage;
